import React from "react";
import { Collapse, Tabs, Tag, Typography, List, Alert, Card, Divider, Flex } from "antd";
import {
    UndoOutlined, FileTextOutlined, SafetyOutlined,
    ClockCircleOutlined, CreditCardOutlined, StopOutlined,
    ReloadOutlined, EnvironmentOutlined, BellOutlined,
    CameraOutlined, FileImageOutlined, UserOutlined,
    IdcardOutlined, PhoneOutlined, BankOutlined,
} from "@ant-design/icons";
import TopNavigation from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";

const { Title, Text, Link, Paragraph } = Typography;

// ── Shared components ─────────────────────────────────────────────────────────

const PolicyHero: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
    <div style={{ background: "#00AF66", borderRadius: "14px 14px 0 0", padding: "28px 28px 24px" }}>
        <Text style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.75)" }}>
            Legal Policy
        </Text>
        <Title level={3} style={{ color: "#fff", margin: "6px 0 4px" }}>{title}</Title>
        <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>{subtitle} · Last updated June 2026</Text>
    </div>
);

const LawBadge: React.FC<{ text: string }> = ({ text }) => (
    <Tag color="green" style={{ marginBottom: 6 }}>{text}</Tag>
);

const SectionHeader: React.FC<{ number: string; title: string }> = ({ number, title }) => (
    <Flex align="center" gap={12} style={{ margin: "1.75rem 0 1rem" }}>
        <div style={{
            width: 32, height: 32, borderRadius: "50%", background: "#00AF66",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 800, flexShrink: 0,
        }}>
            {number}
        </div>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <div style={{ flex: 1, height: 2, background: "#E6F7EF", borderRadius: 1 }} />
    </Flex>
);

const InfoBox: React.FC<{ children: React.ReactNode; type?: "warning" | "brand" }> = ({ children, type = "brand" }) => (
    <Alert type={type === "warning" ? "warning" : "success"} showIcon={false} style={{ marginBottom: 12 }} description={children} />
);

// ── Privacy Policy Tab ────────────────────────────────────────────────────────

const PrivacyTab: React.FC = () => (
    <div>
        <PolicyHero title="Privacy Policy" subtitle="How Quick Quick collects and uses your data" />
        <Card style={{ borderRadius: "0 0 14px 14px", borderTop: "none" }}>
            <Flex wrap="wrap" gap={4} style={{ marginBottom: 16 }}>
                <LawBadge text="POPIA 4 of 2013" />
                <LawBadge text="ECTA 25 of 2002" />
                <LawBadge text="Google Play Policy" />
                <LawBadge text="Apple App Store Policy" />
            </Flex>

            <InfoBox type="brand">
                <Text strong>Quick Quick</Text> is a food delivery platform operated by{" "}
                <Text strong>Hlawulani Leonel Greenwich (Pty) Ltd</Text>, registered in South Africa.
                This policy explains exactly what data we collect, why we need it, and how we protect it.
                We only collect what is strictly necessary to operate the service.
            </InfoBox>

            {/* ── Section 1: Who this applies to ── */}
            <SectionHeader number="1" title="Who This Policy Applies To" />
            <List size="small" dataSource={[
                { role: "Customers",    desc: "People ordering food through the app" },
                { role: "Drivers",      desc: "Independent couriers delivering orders" },
                { role: "Restaurant partners", desc: "Vendors listing their menu on the platform" },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta title={<Text strong>{item.role}</Text>} description={item.desc} />
                </List.Item>
            )} />

            {/* ── Section 2: Location ── */}
            <SectionHeader number="2" title="Location — Why We Need It" />
            <InfoBox type="warning">
                Location is the core of a delivery app. Without it, we cannot show nearby restaurants,
                calculate delivery distances, or let customers track their driver in real time.
            </InfoBox>
            <List size="small" dataSource={[
                {
                    icon: <EnvironmentOutlined style={{ color: "#00AF66" }} />,
                    title: "Foreground location (customers)",
                    desc: "Used when you open the app to show restaurants near you and give the driver your delivery address. Only active while you are using the app.",
                },
                {
                    icon: <EnvironmentOutlined style={{ color: "#E53935" }} />,
                    title: "Foreground location (drivers)",
                    desc: "Used while the app is open to update your position on the customer's tracking map and calculate the delivery route.",
                },
                {
                    icon: <EnvironmentOutlined style={{ color: "#FB8C00" }} />,
                    title: "Background location (drivers only)",
                    desc: "Used when the app is minimised during an active delivery so the customer can still see where their order is. Background location is ONLY active while a delivery is in progress and stops automatically when the order is marked delivered. It is never collected when no delivery is active.",
                },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        avatar={item.icon}
                        title={<Text strong>{item.title}</Text>}
                        description={item.desc}
                    />
                </List.Item>
            )} />

            {/* ── Section 3: Background processing ── */}
            <SectionHeader number="3" title="Background Processing & Notifications — Why We Need It" />
            <InfoBox type="brand">
                A delivery app must be able to alert drivers to new orders and customers to delivery
                updates even when the app is not open. Without this, orders would be missed.
            </InfoBox>
            <List size="small" dataSource={[
                {
                    icon: <BellOutlined style={{ color: "#00AF66" }} />,
                    title: "Push notifications",
                    desc: "We send notifications to drivers when a new order is available for acceptance, and to customers when their order is confirmed, picked up, and delivered. Powered by Firebase Cloud Messaging.",
                },
                {
                    icon: <BellOutlined style={{ color: "#00AF66" }} />,
                    title: "Foreground service",
                    desc: "Android requires a foreground service to keep the delivery tracking running reliably while the driver has the app in the background. A persistent notification is shown to the driver while this service is active, in compliance with Android policy.",
                },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        avatar={item.icon}
                        title={<Text strong>{item.title}</Text>}
                        description={item.desc}
                    />
                </List.Item>
            )} />

            {/* ── Section 4: File uploads ── */}
            <SectionHeader number="4" title="Camera & File Uploads — Why We Need It" />
            <List size="small" dataSource={[
                {
                    icon: <IdcardOutlined style={{ color: "#00AF66" }} />,
                    title: "Driver legal documents",
                    desc: "Drivers must upload a photo of their driver's licence, South African ID or passport, and optionally a bank statement. These are required by law to verify identity before a driver can accept deliveries. Files are stored securely and only accessed by our compliance team.",
                },
                {
                    icon: <FileImageOutlined style={{ color: "#00AF66" }} />,
                    title: "Restaurant food menu photos",
                    desc: "Restaurant partners upload photos of their menu items so customers can see what they are ordering. This requires access to the device photo library.",
                },
                {
                    icon: <CameraOutlined style={{ color: "#00AF66" }} />,
                    title: "Camera access",
                    desc: "Used to take photos directly when uploading documents or menu item images. We never access the camera without the user actively tapping an upload button.",
                },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        avatar={item.icon}
                        title={<Text strong>{item.title}</Text>}
                        description={item.desc}
                    />
                </List.Item>
            )} />

            {/* ── Section 5: Personal data collected ── */}
            <SectionHeader number="5" title="Personal Data We Collect" />

            <Text strong style={{ display: "block", marginBottom: 8 }}>All users:</Text>
            <List size="small" dataSource={[
                "Full name and surname",
                "Email address",
                "Phone number",
                "Delivery address",
                "Password (encrypted, never stored in plain text)",
            ]} renderItem={(item) => <List.Item>{item}</List.Item>} />

            <Text strong style={{ display: "block", margin: "16px 0 8px" }}>Drivers only (KYC verification):</Text>
            <List size="small" dataSource={[
                "South African ID or passport number",
                "Driver's licence number",
                "Vehicle make, model and registration number",
                "Profile photo",
                "Bank account details (for weekly payouts)",
                "Driver's licence document photo",
                "ID / passport document photo",
                "Bank statement (optional, for account verification)",
            ]} renderItem={(item) => <List.Item>{item}</List.Item>} />

            {/* ── Section 6: Advertising ID ── */}
            <SectionHeader number="6" title="Advertising ID (AD_ID)" />
            <InfoBox type="brand">
                <Text strong>We do not use your Advertising ID.</Text> The AD_ID permission is explicitly
                blocked in our app. Quick Quick contains no advertisements, does not track you for
                marketing purposes, and does not share your data with any advertising networks.
            </InfoBox>

            {/* ── Section 7: Third parties ── */}
            <SectionHeader number="7" title="Third-Party Services We Use" />
            <List size="small" dataSource={[
                { name: "Paystack",                 why: "Secure payment processing and driver payout transfers" },
                { name: "Google Maps Platform",     why: "Map display, delivery route calculation, and distance estimation" },
                { name: "Firebase (Google)",        why: "Push notifications (FCM) and crash/error reporting" },
                { name: "Nominatim / OpenStreetMap", why: "Converting GPS coordinates to a readable address" },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        title={<Text strong>{item.name}</Text>}
                        description={item.why}
                    />
                </List.Item>
            )} />
            <Paragraph type="secondary" style={{ marginTop: 8 }}>
                We do not sell your data. Data is only shared with the above providers to the minimum
                extent necessary to operate the service.
            </Paragraph>

            {/* ── Section 8: Your rights ── */}
            <SectionHeader number="8" title="Your Rights Under POPIA" />
            <InfoBox type="brand">
                <Text strong>You have the right to:</Text>
                <List size="small" style={{ marginTop: 8 }} dataSource={[
                    <><Text strong>Access</Text> — request a copy of the personal data we hold about you</>,
                    <><Text strong>Correct</Text> — update inaccurate information in your profile</>,
                    <><Text strong>Delete</Text> — permanently delete your account and data via Settings → Delete Account in the app</>,
                    <><Text strong>Object</Text> — object to how we process your data</>,
                    <><Text strong>Complain</Text> — lodge a complaint with the Information Regulator of South Africa at <Link href="https://inforeg.org.za" target="_blank">inforeg.org.za</Link></>,
                ]} renderItem={(item) => <List.Item>{item}</List.Item>} />
            </InfoBox>

            {/* ── Section 9: Retention ── */}
            <SectionHeader number="9" title="How Long We Keep Your Data" />
            <List size="small" dataSource={[
                { period: "Account data",         ret: "Kept while your account is active. Deleted within 30 days of account deletion." },
                { period: "Order history",        ret: "Kept for 5 years for financial compliance under South African law." },
                { period: "Driver KYC documents", ret: "Kept for the duration of the driver's active registration plus 5 years for legal compliance." },
                { period: "Location data",        ret: "Live location is not stored. It is transmitted in real time and discarded once the delivery is complete." },
                { period: "Crash / error logs",   ret: "Kept for 90 days for debugging purposes." },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        title={<Text strong>{item.period}</Text>}
                        description={item.ret}
                    />
                </List.Item>
            )} />

            {/* ── Section 10: Children ── */}
            <SectionHeader number="10" title="Children's Privacy" />
            <Paragraph type="secondary">
                Quick Quick is not intended for anyone under <Text strong>18 years of age</Text>.
                We do not knowingly collect data from minors. If you believe a minor has registered,
                contact us immediately and we will delete the account.
            </Paragraph>

            {/* ── Section 11: Contact ── */}
            <SectionHeader number="11" title="Contact Us" />
            <Paragraph type="secondary">
                For any privacy questions, data requests, or complaints contact our Information Officer at:
            </Paragraph>
            <List size="small" dataSource={[
                { label: "Email",   value: <Link href="mailto:privacy@deliveryquickquick.co.za">privacy@deliveryquickquick.co.za</Link> },
                { label: "Website", value: <Link href="https://hlgreenwich.co.za" target="_blank">hlgreenwich.co.za</Link> },
                { label: "Company", value: "Hlawulani Leonel Greenwich (Pty) Ltd, South Africa" },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta title={<Text strong>{item.label}</Text>} description={item.value} />
                </List.Item>
            )} />

            <Divider />
            <Text type="secondary" style={{ display: "block", textAlign: "center", fontSize: 12 }}>
                This policy is publicly accessible at{" "}
                <Link href="https://deliveryquickquick.co.za/policy" target="_blank">
                    deliveryquickquick.co.za/policy
                </Link>
                {" "}and meets Google Play and Apple App Store privacy requirements.
            </Text>
        </Card>
    </div>
);

// ── Refund Tab ────────────────────────────────────────────────────────────────

const RefundTab: React.FC = () => (
    <div>
        <PolicyHero title="Refund, Return & Cancellation Policy" subtitle="Consumer Protection Act 68 of 2008" />
        <Card style={{ borderRadius: "0 0 14px 14px", borderTop: "none" }}>
            <Flex wrap="wrap" gap={4} style={{ marginBottom: 16 }}>
                <LawBadge text="CPA 68 of 2008" />
                <LawBadge text="ECTA 25 of 2002" />
            </Flex>

            <SectionHeader number="1" title="Cancellation Policy" />
            <Collapse items={[
                {
                    key: "0", label: "By the Customer",
                    children: (
                        <>
                            <Paragraph type="secondary">
                                Customers may cancel an order <Text strong>only before the driver has accepted and commenced the delivery</Text>.
                                Once a driver is en route, cancellation is not permitted and no refund will be issued,
                                except where the vendor failed to prepare the order within a reasonable time.
                            </Paragraph>
                            <Paragraph type="secondary">
                                In accordance with <Text strong>Section 17 of the Consumer Protection Act</Text>,
                                customers who book in advance may cancel with reasonable notice.
                            </Paragraph>
                        </>
                    ),
                },
                {
                    key: "1", label: "By the Driver",
                    children: (
                        <Paragraph type="secondary">
                            Drivers may cancel only in exceptional circumstances such as vehicle breakdown or a safety concern.
                            Repeated cancellations may result in suspension from the platform.
                        </Paragraph>
                    ),
                },
                {
                    key: "2", label: "By the Platform",
                    children: (
                        <Paragraph type="secondary">
                            We may cancel where fraud is suspected or the vendor cannot fulfill the order.
                            In such cases a <Text strong>full refund will be issued</Text>.
                        </Paragraph>
                    ),
                },
            ]} />

            <SectionHeader number="2" title="Refund Policy" />
            <InfoBox type="brand">
                <Text strong>Full refund is issued when:</Text>
                <List size="small" style={{ marginTop: 8 }} dataSource={[
                    "Order cancelled before driver acceptance",
                    "Vendor failed to prepare the order",
                    "Platform cancelled due to circumstances beyond the customer's control",
                    "Wrong items were delivered",
                    "Order was significantly late beyond communicated timeframe",
                ]} renderItem={(item) => <List.Item>{item}</List.Item>} />
            </InfoBox>

            <Flex gap="middle" wrap="wrap" style={{ marginTop: 16 }}>
                <Card style={{ flex: 1, minWidth: 220 }} title="Refund Process">
                    <List size="small" dataSource={[
                        { icon: <ClockCircleOutlined />, text: <><Text strong>24 hours</Text> to report via app</> },
                        { icon: <ReloadOutlined />,      text: <>Processed in <Text strong>5–7 business days</Text></> },
                        { icon: <CreditCardOutlined />,  text: "Returned via Paystack to original payment method" },
                    ]} renderItem={(item) => (
                        <List.Item><List.Item.Meta avatar={item.icon} description={item.text} /></List.Item>
                    )} />
                </Card>
                <Card style={{ flex: 1, minWidth: 220 }} title="Non-Refundable">
                    <List size="small" dataSource={[
                        "R10 platform fee when delivery was completed",
                        "Orders cancelled after driver acceptance without valid reason",
                    ]} renderItem={(item) => (
                        <List.Item><List.Item.Meta avatar={<StopOutlined style={{ color: "orange" }} />} description={item} /></List.Item>
                    )} />
                </Card>
            </Flex>

            <SectionHeader number="3" title="Return Policy" />
            <InfoBox type="warning">
                Given the perishable nature of food, <Text strong>we do not accept physical returns</Text>.
                All disputes must be reported in-app within 24 hours with supporting photos.
            </InfoBox>
        </Card>
    </div>
);

// ── Terms Tab ─────────────────────────────────────────────────────────────────

const TermsTab: React.FC = () => (
    <div>
        <PolicyHero title="Terms of Service" subtitle="Governed by the laws of the Republic of South Africa" />
        <Card style={{ borderRadius: "0 0 14px 14px", borderTop: "none" }}>
            <Flex wrap="wrap" gap={4} style={{ marginBottom: 16 }}>
                <LawBadge text="CPA 68 of 2008" />
                <LawBadge text="ECTA 25 of 2002" />
                <LawBadge text="POPIA 4 of 2013" />
                <LawBadge text="Income Tax Act" />
            </Flex>

            {[
                {
                    num: "1", title: "Introduction",
                    content: <Paragraph type="secondary">
                        These Terms govern your use of the Quick Quick platform. By registering you agree to
                        be bound by them. They are governed by the laws of the <Text strong>Republic of South Africa</Text>.
                    </Paragraph>,
                },
                {
                    num: "2", title: "Eligibility",
                    content: <List size="small" dataSource={[
                        <>Must be at least <Text strong>18 years of age</Text></>,
                        "Must be located within the Republic of South Africa",
                        "Must provide accurate registration information",
                        "Must hold a valid South African ID, passport, or equivalent",
                    ]} renderItem={(item) => <List.Item>{item}</List.Item>} />,
                },
                {
                    num: "3", title: "Driver Terms",
                    content: <>
                        <InfoBox type="warning">
                            <Text strong>Drivers are independent contractors — not employees of the Platform.</Text>
                        </InfoBox>
                        <List size="small" dataSource={[
                            "Drivers set their own per-kilometre delivery rate, visible to customers before confirmation",
                            "Must hold a valid driver's licence and roadworthy vehicle",
                            "Consent to GPS tracking (foreground and background) during active deliveries",
                            "Payouts processed to registered bank account after delivery, net of R10 platform fee",
                            "Responsible for their own tax obligations under the South African Income Tax Act",
                        ]} renderItem={(item) => <List.Item>{item}</List.Item>} />
                    </>,
                },
                {
                    num: "4", title: "Payment Terms",
                    content: <Flex vertical gap={8}>
                        {[
                            "All payments processed securely via Paystack in ZAR",
                            "Full payment collected upfront at order placement",
                            "Platform retains R10 service fee per completed order",
                            "Driver receives distance × rate upon confirmed delivery",
                            "Electronic payments are legally binding under ECTA 25 of 2002",
                        ].map((text, i) => <Alert key={i} type="success" message={text} showIcon={false} />)}
                    </Flex>,
                },
                {
                    num: "5", title: "Limitation of Liability",
                    content: <List size="small" dataSource={[
                        "The Platform acts as an intermediary between customers, drivers, and vendors",
                        "Not liable for delays caused by traffic, weather, or force majeure",
                        "Total liability per order shall not exceed the amount paid for that order",
                        "Nothing limits liability for death, personal injury, or fraud caused by our negligence per the CPA",
                    ]} renderItem={(item) => <List.Item>{item}</List.Item>} />,
                },
                {
                    num: "6", title: "Dispute Resolution",
                    content: <Flex vertical gap={10}>
                        {[
                            { step: "1", label: "Contact in-app support first" },
                            { step: "2", label: "Unresolved within 7 business days → National Consumer Commission (NCC)" },
                            { step: "3", label: "Escalate to arbitration or a competent South African court" },
                        ].map((item) => (
                            <Flex key={item.step} gap={12} align="flex-start">
                                <div style={{
                                    width: 28, height: 28, borderRadius: "50%", background: "#00AF66",
                                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 12, fontWeight: 800, flexShrink: 0,
                                }}>{item.step}</div>
                                <Paragraph type="secondary" style={{ margin: 0, paddingTop: 4 }}>{item.label}</Paragraph>
                            </Flex>
                        ))}
                    </Flex>,
                },
            ].map((section) => (
                <div key={section.num}>
                    <SectionHeader number={section.num} title={section.title} />
                    {section.content}
                    <Divider />
                </div>
            ))}
        </Card>
    </div>
);

// ── KYC Tab ───────────────────────────────────────────────────────────────────

const KycTab: React.FC = () => (
    <div>
        <PolicyHero title="KYC & Driver Verification" subtitle="Protection of Personal Information Act 4 of 2013" />
        <Card style={{ borderRadius: "0 0 14px 14px", borderTop: "none" }}>
            <Flex wrap="wrap" gap={4} style={{ marginBottom: 16 }}>
                <LawBadge text="POPIA 4 of 2013" />
                <LawBadge text="ECTA 25 of 2002" />
            </Flex>

            <InfoBox type="brand">
                Before a driver can accept any deliveries, we are legally and operationally required to
                verify their identity. This protects customers, restaurants, and the platform from fraud
                and ensures only legitimate, licenced couriers operate on Quick Quick.
            </InfoBox>

            <SectionHeader number="1" title="Documents We Collect from Drivers" />
            <Flex wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
                {[
                    { icon: <UserOutlined />,     label: "Full name",                sub: "As per ID or passport" },
                    { icon: <PhoneOutlined />,    label: "Contact details",          sub: "Phone number & email" },
                    { icon: <IdcardOutlined />,   label: "SA ID / Passport",         sub: "Identity verification" },
                    { icon: <UserOutlined />,     label: "Profile photo",            sub: "Driver identification" },
                    { icon: <BankOutlined />,     label: "Bank account details",     sub: "For weekly payouts" },
                    { icon: <FileImageOutlined />,label: "Vehicle information",      sub: "Make, model & registration" },
                    { icon: <IdcardOutlined />,   label: "Driver's licence",         sub: "Verified against NATIS" },
                ].map((item, i) => (
                    <Card key={i} size="small" style={{ minWidth: 180, flex: 1 }}>
                        <List.Item.Meta
                            avatar={<span style={{ color: "#00AF66" }}>{item.icon}</span>}
                            title={item.label}
                            description={item.sub}
                        />
                    </Card>
                ))}
            </Flex>

            <SectionHeader number="2" title="Why We Need Each Document" />
            <List size="small" dataSource={[
                { doc: "Driver's licence",    why: "To confirm the driver is legally permitted to operate a motor vehicle on South African roads." },
                { doc: "SA ID / Passport",    why: "To verify the driver's identity and ensure they are who they claim to be." },
                { doc: "Bank account details", why: "To process weekly earnings payouts via Paystack securely to the correct person." },
                { doc: "Bank statement",      why: "Optional — used to confirm bank account ownership during onboarding." },
                { doc: "Vehicle information", why: "To ensure the vehicle is appropriate for deliveries and to display to customers for safety." },
                { doc: "Profile photo",       why: "So customers can identify their driver when they arrive for pickup or delivery." },
            ]} renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        title={<Text strong>{item.doc}</Text>}
                        description={item.why}
                    />
                </List.Item>
            )} />

            <SectionHeader number="3" title="Your Rights Under POPIA" />
            <InfoBox type="brand">
                <Text strong>In accordance with POPIA Section 23, you have the right to:</Text>
                <List size="small" style={{ marginTop: 8 }} dataSource={[
                    <><Text strong>Access</Text> your personal information held by us</>,
                    <><Text strong>Correct</Text> inaccurate personal information</>,
                    <><Text strong>Delete</Text> your account and data via Settings → Delete Account</>,
                    <><Text strong>Object</Text> to how we process your information</>,
                    <>Complain to the <Text strong>Information Regulator</Text> at <Link href="https://inforeg.org.za" target="_blank">inforeg.org.za</Link></>,
                ]} renderItem={(item) => <List.Item>{item}</List.Item>} />
            </InfoBox>

            <SectionHeader number="4" title="Document Security" />
            <Paragraph type="secondary">
                All uploaded documents are transmitted over encrypted HTTPS connections and stored in
                secure cloud storage with access restricted to authorised compliance staff only.
                Documents are retained for the duration of the driver's registration plus 5 years
                as required by South African financial and tax law.
            </Paragraph>

            <Divider />
            <Text type="secondary" style={{ display: "block", textAlign: "center", fontSize: 12 }}>
                Questions about your data? Contact{" "}
                <Link href="mailto:privacy@deliveryquickquick.co.za">privacy@deliveryquickquick.co.za</Link>
            </Text>
        </Card>
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const PoliciesPage: React.FC = () => (
    <>
        <TopNavigation />
        <div style={{ background: "#F7F9FC", minHeight: "100vh", paddingTop: 72 }}>
            <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px 48px" }}>
                <Tabs
                    centered
                    items={[
                        { key: "privacy",  label: <><SafetyOutlined /> Privacy Policy</>,          children: <PrivacyTab /> },
                        { key: "refund",   label: <><UndoOutlined /> Refund & Cancellation</>,     children: <RefundTab /> },
                        { key: "tos",      label: <><FileTextOutlined /> Terms of Service</>,      children: <TermsTab /> },
                        { key: "kyc",      label: <><IdcardOutlined /> KYC & Verification</>,      children: <KycTab /> },
                    ]}
                />
            </div>
        </div>
        <Footer />
    </>
);

export default PoliciesPage;